import React, { useState, useEffect } from 'react';
import { BarChart3, Users, AlertTriangle } from 'lucide-react';
import api from '../api';
import { Breadcrumbs } from './Breadcrumbs';

const TIMEFRAMES = ['now', 'next', 'later', 'Q1', 'Q2', 'Q3', 'Q4'];

function CapacityPlanning({ user }) {
  const [roadmaps, setRoadmaps] = useState([]);
  const [selectedRoadmap, setSelectedRoadmap] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [capacityLimit, setCapacityLimit] = useState(5);

  useEffect(() => {
    loadRoadmaps();
  }, []);

  useEffect(() => {
    if (selectedRoadmap) loadItems();
  }, [selectedRoadmap]);

  const loadRoadmaps = async () => {
    try {
      const data = await api.getRoadmaps();
      setRoadmaps(data);
      if (data.length > 0) setSelectedRoadmap(data[0]._id);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadItems = async () => {
    try {
      const data = await api.getItems(selectedRoadmap);
      setItems(data);
    } catch (e) { console.error(e); }
  };

  // Group by owner
  const ownerMap = {};
  items.forEach(item => {
    const owner = item.owner || 'Unassigned';
    if (!ownerMap[owner]) ownerMap[owner] = {};
    const tf = item.timeframe || 'now';
    if (!ownerMap[owner][tf]) ownerMap[owner][tf] = [];
    ownerMap[owner][tf].push(item);
  });

  const owners = Object.keys(ownerMap).sort();
  const activeTimeframes = TIMEFRAMES.filter(tf => items.some(i => i.timeframe === tf));
  if (activeTimeframes.length === 0 && items.length > 0) activeTimeframes.push('now');

  const getLoadColor = (count) => {
    if (count >= capacityLimit) return 'bg-destructive/20 text-destructive border-destructive/30';
    if (count >= capacityLimit * 0.7) return 'bg-amber-500/15 text-amber-600 border-amber-500/30';
    return 'bg-primary/10 text-primary border-primary/20';
  };

  const breadcrumbs = [
    { label: 'Dashboard', path: '/' },
    { label: 'Capacity Planning', path: '/capacity' },
  ];

  return (
    <div className="min-h-full" data-testid="capacity-planning">
      <div className="px-6 lg:px-8 pt-6 pb-2">
        <Breadcrumbs items={breadcrumbs} />
      </div>
      <div className="px-6 lg:px-8 pt-4 pb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Capacity Planning</h1>
            <p className="text-sm text-muted-foreground mt-1">Visualize team workload across timeframes</p>
          </div>
          <div className="flex items-center gap-3">
            <select value={selectedRoadmap} onChange={(e) => setSelectedRoadmap(e.target.value)} className="px-4 py-2 border border-input rounded-xl bg-background text-sm" data-testid="roadmap-selector">
              {roadmaps.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
            </select>
            <div className="flex items-center gap-2">
              <label className="text-xs text-muted-foreground whitespace-nowrap">Capacity limit:</label>
              <input type="number" value={capacityLimit} onChange={(e) => setCapacityLimit(parseInt(e.target.value) || 1)} min={1} max={20} className="w-16 px-2 py-2 border border-input rounded-xl bg-background text-sm text-center font-mono-score" data-testid="capacity-limit" />
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 lg:px-8 pb-8">
        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <div className="bg-card border border-border rounded-xl p-4">
            <p className="text-xs text-muted-foreground mb-1">Total Items</p>
            <p className="text-2xl font-bold font-mono-score">{items.length}</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <p className="text-xs text-muted-foreground mb-1">Team Members</p>
            <p className="text-2xl font-bold font-mono-score">{owners.length}</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <p className="text-xs text-muted-foreground mb-1">Overloaded</p>
            <p className="text-2xl font-bold font-mono-score text-destructive">
              {owners.filter(o => Object.values(ownerMap[o]).some(arr => arr.length >= capacityLimit)).length}
            </p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <p className="text-xs text-muted-foreground mb-1">Avg per Person</p>
            <p className="text-2xl font-bold font-mono-score">{owners.length ? (items.length / owners.length).toFixed(1) : 0}</p>
          </div>
        </div>

        {/* Capacity grid */}
        {items.length === 0 ? (
          <div className="text-center py-16 bg-card border border-border rounded-xl">
            <BarChart3 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Items to Analyze</h3>
            <p className="text-sm text-muted-foreground">Select a roadmap with items to see capacity data</p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="capacity-table">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider sticky left-0 bg-card z-10 min-w-[160px]">Owner</th>
                    {activeTimeframes.map(tf => (
                      <th key={tf} className="text-center px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider min-w-[120px]">{tf}</th>
                    ))}
                    <th className="text-center px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider min-w-[80px]">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {owners.map(owner => {
                    const total = Object.values(ownerMap[owner]).reduce((s, arr) => s + arr.length, 0);
                    return (
                      <tr key={owner} className="border-b border-border hover:bg-muted/30 transition-colors" data-testid={`capacity-row-${owner}`}>
                        <td className="px-5 py-3 sticky left-0 bg-card z-10">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center text-primary text-xs font-bold">{owner.charAt(0).toUpperCase()}</div>
                            <span className="text-sm font-medium truncate max-w-[120px]">{owner === 'Unassigned' ? 'Unassigned' : owner.split('@')[0]}</span>
                          </div>
                        </td>
                        {activeTimeframes.map(tf => {
                          const count = (ownerMap[owner][tf] || []).length;
                          return (
                            <td key={tf} className="px-5 py-3 text-center">
                              {count > 0 ? (
                                <span className={`inline-flex items-center justify-center min-w-[32px] px-2 py-1 rounded-lg text-sm font-semibold font-mono-score border ${getLoadColor(count)}`}>
                                  {count}
                                  {count >= capacityLimit && <AlertTriangle className="h-3 w-3 ml-1" />}
                                </span>
                              ) : (
                                <span className="text-xs text-muted-foreground/40">-</span>
                              )}
                            </td>
                          );
                        })}
                        <td className="px-5 py-3 text-center">
                          <span className={`inline-flex items-center justify-center min-w-[32px] px-2 py-1 rounded-lg text-sm font-bold font-mono-score border ${getLoadColor(total)}`}>{total}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="mt-4 flex items-center gap-6 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-primary/20 border border-primary/30" /> Under capacity</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-500/20 border border-amber-500/30" /> Near capacity</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-destructive/20 border border-destructive/30" /> Overloaded</span>
        </div>
      </div>
    </div>
  );
}

export default CapacityPlanning;
